import React, { useState } from "react";
import { X, MapPin, Shield, Trophy, Calendar, MessageSquare, IdCard } from "lucide-react";
import { sportInfo, normalizeWa, positionInfo } from "../lib/api";
import PlayerCardShare from "./PlayerCardShare";

/**
 * Modal showing a player's public profile from inside Match Detail.
 * Props:
 *   player: { user_id, name, username, picture, city, whatsapp,
 *             sports, skill_levels, reliability_score, matches_played, matches_attended }
 *   isHost: bool (decorates badge if this player is the host)
 *   onClose()
 */
export default function PlayerProfileModal({ player, isHost = false, onClose }) {
  const [showCard, setShowCard] = useState(false);
  if (!player) return null;
  const sports = player.sports || [];
  const skillLevels = player.skill_levels || {};
  const positions = player.positions || {};
  const reliability = Number(player.reliability_score ?? 100);
  const played = player.matches_played ?? 0;
  const attended = player.matches_attended ?? 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="player-profile-modal"
    >
      <div
        className="w-full max-w-md bg-card border border-border rounded-t-3xl sm:rounded-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-2">
          <div className="text-xs text-muted-foreground font-bold uppercase tracking-wider">Profil Pemain</div>
          <button
            onClick={onClose}
            data-testid="player-profile-close"
            className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Hero */}
        <div className="px-5 pb-3 flex items-center gap-4">
          <div className="h-20 w-20 rounded-full overflow-hidden bg-secondary border-2 border-primary/40 glow-cyan shrink-0">
            {player.picture
              ? <img src={player.picture} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center font-bold text-2xl">{(player.name || "?")[0]}</div>}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h2 className="font-heading text-xl font-black tracking-tight truncate">{player.name}</h2>
              {isHost && <span className="text-[9px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">host</span>}
            </div>
            {player.username && (
              <div className="text-xs text-muted-foreground">@{player.username}</div>
            )}
            {player.city && (
              <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {player.city}
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="px-5 mt-2 grid grid-cols-3 gap-2">
          <Stat icon={Trophy}   label="Match"       value={played} />
          <Stat icon={Calendar} label="Hadir"       value={attended} />
          <Stat icon={Shield}   label="Reliability" value={`${reliability.toFixed(0)}%`} />
        </div>

        {/* Sports */}
        {sports.length > 0 && (
          <div className="px-5 mt-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Olahraga</div>
            <div className="flex flex-wrap gap-2">
              {sports.map((sid) => {
                const s = sportInfo(sid);
                const level = skillLevels[sid] || "intermediate";
                const pos = positionInfo(sid, positions[sid]);
                return (
                  <div key={sid} className="bg-secondary border border-border rounded-full px-3 py-1.5 flex items-center gap-1.5">
                    <span className="text-sm">{s.icon}</span>
                    <span className="text-xs font-bold">{s.label}</span>
                    <span className="text-[9px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">{level}</span>
                    {pos && (
                      <span data-testid={`player-position-${sid}`} className="text-[9px] font-bold uppercase tracking-wider text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded-full">{pos.short}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* WhatsApp CTA */}
        <div className="px-5 pt-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] space-y-2">
          {player.whatsapp ? (
            <a
              href={`https://wa.me/${normalizeWa(player.whatsapp)}?text=${encodeURIComponent(`Halo ${player.name}, soal match nih`)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="player-profile-wa"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-emerald-500 text-black font-bold active:scale-95 transition-transform"
            >
              <MessageSquare className="h-4 w-4" /> Chat via WhatsApp
            </a>
          ) : (
            <div className="w-full inline-flex items-center justify-center px-5 py-3.5 rounded-full bg-secondary border border-border text-muted-foreground text-sm font-bold">
              Pemain belum menambahkan nomor WhatsApp
            </div>
          )}
          <button
            type="button"
            onClick={() => setShowCard(true)}
            data-testid="player-profile-card-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-secondary border border-border font-bold text-foreground hover:border-primary/40"
          >
            <IdCard className="h-4 w-4" /> Bagikan Kartu Pemain
          </button>
        </div>
      </div>
      {showCard && (
        <PlayerCardShare player={player} onClose={() => setShowCard(false)} />
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-secondary/60 border border-border rounded-2xl p-3 text-center">
      <Icon className="h-4 w-4 text-primary mx-auto" />
      <div className="font-heading text-xl font-black mt-1">{value}</div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}
